/**
 * Resolve o id de um item no arquivo de ícone.
 *
 * A cadeia é sempre a mesma, e nesta ordem:
 *   1. PNG em `public/sprites/itens/**`     (`cavalo-baio` → `itens/animals/cavalo-baio.png`)
 *   2. arquivo SVG com o nome exato do id   (`black-pearl`, `plate-helm`)
 *   3. arquétipo, para armas                (`viking-sword` → `sword`)
 *   4. `_default`, que mostra "?"           (nunca devolve string vazia)
 *
 * O passo 4 é o que garante que um item novo no backend aparece na tela com um
 * espaço reservado em vez de um ícone quebrado — e que dá para ver a olho nu
 * quais itens ainda não têm arte.
 */

import { baseDe, ehCouroOuOsso, ehMateriaPrima, ehMinerioOuLingote, MATERIAIS } from '../../look/materiais';
import { ehMobilia } from '../../data/mobiliaInfo';
import { iconeDaMontaria, MONTARIAS } from '../../world/montarias';

// `eager` porque são 70 arquivos de ~4 KB: importar tudo custa menos que 70
// requisições, e o Vite ainda faz hash e cache de cada um.
const arquivos = import.meta.glob<string>('./**/*.svg', {
  eager: true,
  query: '?url',
  import: 'default'
});

/**
 * PNG em `public/sprites/itens/**`, servido como URL estática.
 * Não importar esses arquivos: o Vite recusa glob de `public/`.
 */
export const PILE_PRATA = '/sprites/itens/coin/silver_pile.png';
export const PILE_OURO = '/sprites/itens/coin/gold_pile.png';

const ITENS_PUBLICOS: Readonly<Record<string, string>> = {
  // As 117 montarias: o ícone sai do vd-para-montaria.py (o primeiro quadro parado, de lado).
  ...Object.fromEntries(MONTARIAS.map(m => [m.id, iconeDaMontaria(m.id)])),
  robe: '/sprites/itens/cloth/robe.png',
  // Tintas prime do guarda-roupa: a tina de tinta do UO, já na matiz (tools/traje-arte.py).
  'tinta-prime-fogo': '/sprites/itens/traje/tinta-fogo.png',
  'tinta-prime-gelo': '/sprites/itens/traje/tinta-gelo.png',
  'tinta-prime-arcano': '/sprites/itens/traje/tinta-arcano.png',
  'tinta-prime-neve': '/sprites/itens/traje/tinta-neve.png',
  'tinta-prime-sombra': '/sprites/itens/traje/tinta-sombra.png',
  'tinta-prime-ouro': '/sprites/itens/traje/tinta-ouro.png',
  'tinta-prime-negro': '/sprites/itens/traje/tinta-negro.png',
  'tinta-prime-sangue': '/sprites/itens/traje/tinta-sangue.png',
  'tinta-prime-veneno': '/sprites/itens/traje/tinta-veneno.png',
  'tinta-prime-purpura': '/sprites/itens/traje/tinta-purpura.png',
  'tinta-prime-carmim': '/sprites/itens/traje/tinta-carmim.png',
  ...Object.fromEntries(['carvao', 'vinho', 'vermelho', 'mostarda', 'musgo', 'verde', 'anil', 'roxo']
    .map(c => [`tinta-${c}`, `/sprites/itens/traje/tinta-${c}.png`])),
  // Chefe do Mundo (techspec-chefe-mundo, tools/chefe-arte.py): o cristal, o baú e a capa de cada cor, já tingida.
  'fragmento-alma': '/sprites/itens/evento/fragmento-alma.png',
  // Pentagrama das Cinco Orbes (techspec-pentagrama, tools/pentagrama-arte.py): o cristal e a orbe de cada ponta.
  ...Object.fromEntries(['fogo', 'gelo', 'terra', 'ar', 'vazio'].flatMap(c => [
    [`pent-fragmento-${c}`, `/sprites/itens/evento/pent-fragmento-${c}.png`],
    [`pent-orbe-${c}`, `/sprites/itens/evento/pent-orbe-${c}.png`]
  ])),
  'bau-balron': '/sprites/itens/evento/bau-balron.png',
  'capa-balron-rubro': '/sprites/itens/traje/capa-2118.png',
  'capa-balron-brasa': '/sprites/itens/traje/capa-1258.png',
  'capa-balron-glacial': '/sprites/itens/traje/capa-1266.png',
  'capa-balron-geada': '/sprites/itens/traje/capa-1151.png',
  'plate-chest': '/sprites/itens/plates/armor.png',
  'plate-arms': '/sprites/itens/plates/arms.png',
  'plate-gloves': '/sprites/itens/plates/gloves.png',
  'plate-helm': '/sprites/itens/plates/helmet.png',
  'plate-legs': '/sprites/itens/plates/legs.png',
  'studded-helm': '/sprites/itens/studded/helm.png',
  'studded-chest': '/sprites/itens/studded/armor.png',
  'studded-arms': '/sprites/itens/studded/arms.png',
  'studded-gloves': '/sprites/itens/studded/gloves.png',
  'studded-legs': '/sprites/itens/studded/legs.png',
  'bone-chest': '/sprites/itens/bone/armor.png',
  'bone-arms': '/sprites/itens/bone/arms.png',
  'bone-gloves': '/sprites/itens/bone/gloves.png',
  'bone-helm': '/sprites/itens/bone/helm.png',
  'bone-legs': '/sprites/itens/bone/legs.png',
  heater: '/sprites/itens/offhand/shield.png',
  arrow: '/sprites/itens/weapons/arrow_1.png',

  // Reagentes — arte do cliente da UO, aparada e centrada num canvas comum de
  // 27x25 (a pérola negra tem 5x5 px de arte; no canvas cru do dump ela virava
  // um ponto no canto do slot). O de-para id do gráfico → arquivo está em
  // `docs/arte-reagentes-uo.md`, junto com os 14 tiles do bloco que ainda não
  // foram identificados.
  'black-pearl': '/sprites/itens/reagents/black-pearl.png',
  'blood-moss': '/sprites/itens/reagents/blood-moss.png',
  garlic: '/sprites/itens/reagents/garlic.png',
  ginseng: '/sprites/itens/reagents/ginseng.png',
  'mandrake-root': '/sprites/itens/reagents/mandrake-root.png',
  nightshade: '/sprites/itens/reagents/nightshade.png',
  'spider-silk': '/sprites/itens/reagents/spider-silk.png',
  'sulphurous-ash': '/sprites/itens/reagents/sulphurous-ash.png',

  // Armas — PNG da arte da UO (docs/arte-armas-uo.md).
  katana: '/sprites/itens/weapons/katana.png',
  dagger: '/sprites/itens/weapons/dagger.png',
  'large-battle-axe': '/sprites/itens/weapons/large-battle-axe.png',
  'gnarled-staff': '/sprites/itens/weapons/gnarled-staff.png',
  bow: '/sprites/itens/weapons/bow.png',
  'heavy-crossbow': '/sprites/itens/weapons/heavy-crossbow.png',
  silver: PILE_PRATA,
  gold: PILE_OURO,
  'empty-bottle': '/sprites/itens/potions/empty-potion.png',
  bandage: '/sprites/itens/bandage/bandage.png',
  healing: '/sprites/itens/potions/healing.png',
  'greater-healing': '/sprites/itens/potions/greater-healing.png',
  'cure-poison': '/sprites/itens/potions/cure-poison.png',
  mana: '/sprites/itens/potions/mana.png',
  // techspec-pocoes: a garrafa branca do cliente (0x0F09) com o líquido tingido (tools/pocoes-arte.py).
  ...Object.fromEntries(['greater-mana', 'greater-cure', 'refresh', 'total-refresh', 'strength-potion', 'greater-strength',
    'agility-potion', 'greater-agility', 'nightsight', 'magic-resist'].map(id => [id, `/sprites/itens/potions/${id}.png`])),

  // Coleta — arte do cliente, centrada num canvas de 44x44. As de ouro usam o desenho da comum,
  // tingido de Golden (look/materiais.ts).
  hatchet: '/sprites/itens/tools/hatchet.png',
  'golden-hatchet': '/sprites/itens/tools/hatchet.png',
  pickaxe: '/sprites/itens/tools/pickaxe.png',
  'golden-pickaxe': '/sprites/itens/tools/pickaxe.png'
};

export function iconeDaPrata(_qty?: number): string {
  return PILE_PRATA;
}

export function iconeDoOuro(_qty?: number): string {
  return PILE_OURO;
}

export function iconeDaFlecha(qty: number): string {
  if (qty >= 25) return '/sprites/itens/weapons/arrow-pile.png';
  if (qty >= 5) return '/sprites/itens/weapons/arrow-5.png';
  return '/sprites/itens/weapons/arrow_1.png';
}

/** stem do arquivo → URL. `./weapons/katana.svg` vira `katana`. */
const porNome = new Map<string, string>();

for (const [caminho, url] of Object.entries(arquivos)) {
  const nome = caminho.slice(caminho.lastIndexOf('/') + 1, -'.svg'.length);
  const existente = porNome.get(nome);
  if (existente && existente !== url) {
    // Dois arquivos com o mesmo nome em pastas diferentes fariam a resolução
    // depender da ordem do glob. Melhor quebrar alto, na montagem, do que
    // servir o ícone errado em silêncio.
    throw new Error(`Ícone duplicado: "${nome}" aparece em mais de uma pasta.`);
  }
  porNome.set(nome, url);
}

/**
 * Arma → arquétipo de silhueta.
 *
 * As 32 armas do GDD cabem em 15 desenhos. Onde duas armas diferem só em número
 * (dano, velocidade, Força), elas compartilham o ícone: dar silhuetas distintas
 * a coisas mecanicamente parecidas ensina o jogador errado.
 */
const ARQUETIPO: Readonly<Record<string, string>> = {};

function exigir(nome: string): string {
  const url = porNome.get(nome);
  if (!url) throw new Error(`Ícone obrigatório ausente em assets/items: "${nome}.svg".`);
  return url;
}

const PADRAO: string = exigir('_default');

export function iconeDoMinerio(qty: number): string {
  if (qty >= 25) return '/sprites/itens/ores/ore-3.png';
  if (qty >= 5) return '/sprites/itens/ores/ore-2.png';
  return '/sprites/itens/ores/ore-1.png';
}

/** A tora: uma sozinha ou a pilha, a partir de 2 (como no cliente da UO). As coloridas tingem a mesma arte. */
export function iconeDaTora(qty: number): string {
  return qty >= 2 ? '/sprites/itens/gather/log_stack.png' : '/sprites/itens/gather/log_1.png';
}

export function iconeDoLingote(qty: number): string {
  if (qty >= 25) return '/sprites/itens/ores/ingot-3.png';
  if (qty >= 5) return '/sprites/itens/ores/ingot-2.png';
  return '/sprites/itens/ores/ingot-1.png';
}

/**
 * Devolve sempre uma URL utilizável — nunca `undefined`. A variante (`bow@oak`) usa a arte da
 * base; a tora colorida (`oak-log`), a da tora comum. A cor entra depois, em `iconeTingido`.
 */
export function iconePara(id: string, qty?: number): string {
  if (id === 'silver') return iconeDaPrata();
  if (id === 'gold') return iconeDoOuro();
  if (id === 'arrow') return iconeDaFlecha(qty ?? 1);
  if (ehMinerioOuLingote(id)) {
    return id.endsWith('-ore') ? iconeDoMinerio(qty ?? 1) : iconeDoLingote(qty ?? 1);
  }
  if (id === 'log' || (id.endsWith('-log') && ehMateriaPrima(id))) return iconeDaTora(qty ?? 1);
  // Couro e osso: a arte do UO (couro cortado 0x1081, osso 0xF7E); os raros tingem a mesma arte.
  const couroOsso = ehCouroOuOsso(id);
  if (couroOsso) return couroOsso === 'couro' ? '/sprites/itens/gather/leather.png' : '/sprites/itens/gather/bone.png';
  id = baseDe(id);
  // Mobília e ferramentas de ofício (techspec-marceneiro-e-oficios-em-casa): a arte do UO, gerada por mobilia-catalogo.py.
  if (ehMobilia(id)) return `/sprites/itens/mobilia/${id}.png`;
  const publico = ITENS_PUBLICOS[id];
  if (publico) return publico;
  if (id.startsWith('capa-balron-')) return '/sprites/itens/traje/capa.png';
  // A skin extraída do guarda-roupa (techspec-skins §06): o ícone da própria peça, já colorido.
  if (/^skin-[a-z0-9-]{3,44}$/.test(id)) return `/sprites/itens/traje/${id.slice('skin-'.length)}.png`;

  const direto = porNome.get(id);
  if (direto) return direto;

  const arquetipo = ARQUETIPO[id];
  if (arquetipo) {
    const url = porNome.get(arquetipo);
    if (url) return url;
  }
  return PADRAO;
}

export function prefetchIcons(ids: readonly string[]): void {
  for (const id of ids) {
    const img = new Image();
    img.src = iconePara(id);
  }
}

/** `true` quando o item está usando o "?" — útil para auditar o que falta de arte. */
export function temArtePropria(id: string): boolean {
  id = baseDe(id);
  if (ehMateriaPrima(id)) return true;
  if (id in ITENS_PUBLICOS || ehMobilia(id)) return true;
  return porNome.has(id) || (ARQUETIPO[id] !== undefined && porNome.has(ARQUETIPO[id]));
}

/** Todos os ids com arquivo próprio. Ordenado, para telas de catálogo. */
export function idsComArte(): readonly string[] {
  const ids = new Set([
    ...[...porNome.keys()].filter(n => n !== '_default'),
    ...Object.keys(ITENS_PUBLICOS),
    ...MATERIAIS.flatMap(m => (m.bruto ? [m.bruto, m.insumo] : [m.insumo]))
  ]);
  return [...ids].sort();
}
